import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import api from "../services/api";
import { uploadWorkorderAttachment } from "../services/workorderDocumentService";
import NieuweWerkbonModal from "./NieuweWerkbonModal";
import ClientActionBar from "../components/clientDetail/ClientActionBar";
import ClientAssetsTab from "../components/clientDetail/ClientAssetsTab";
import ClientContactsTab from "../components/clientDetail/ClientContactsTab";
import ClientCommunicationTab from "../components/clientDetail/ClientCommunicationTab";
import ClientDetailsTab from "../components/clientDetail/ClientDetailsTab";
import ClientDocumentsTab from "../components/clientDetail/ClientDocumentsTab";
import ClientNotesTab from "../components/clientDetail/ClientNotesTab";
import ClientOverviewTab from "../components/clientDetail/ClientOverviewTab";
import ClientPortalAccessTab from "../components/clientDetail/ClientPortalAccessTab";
import ClientPricingTab from "../components/clientDetail/ClientPricingTab";
import ClientSummaryCard from "../components/clientDetail/ClientSummaryCard";
import ClientSuppliersTab from "../components/clientDetail/ClientSuppliersTab";
import ClientTabs, { DEFAULT_TABS } from "../components/clientDetail/ClientTabs";
import ClientWorkordersTab from "../components/clientDetail/ClientWorkordersTab";
import QuickActionContactModal from "../components/clientDetail/QuickActionContactModal";
import QuickActionDocumentModal from "../components/clientDetail/QuickActionDocumentModal";
import QuickActionEditClientModal from "../components/clientDetail/QuickActionEditClientModal";
import ClientImportModal from "../components/clientDetail/ClientImportModal";
import QuickActionProjectModal from "../components/clientDetail/QuickActionProjectModal";
import QuickActionSingleExportModal from "../components/clientDetail/QuickActionSingleExportModal";
import { clientDetailMockData } from "../data/clientDetailMockData";
import { exportClientHub } from "../utils/clientExport";
import {
  createSupplier as createProcurementSupplier,
  listSuppliers as listProcurementSuppliers,
  updateSupplier as updateProcurementSupplier,
} from "../services/procurementService";
import { useUser } from "../context/UserContext";

const TAB_COMPONENTS = {
  overzicht: ClientOverviewTab,
  details: ClientDetailsTab,
  commercieel: ClientPricingTab,
  contactpersonen: ClientContactsTab,
  leveranciers: ClientSuppliersTab,
  assets: ClientAssetsTab,
  werkbonnen: ClientWorkordersTab,
  communicatie: ClientCommunicationTab,
  documenten: ClientDocumentsTab,
  portaaltoegang: ClientPortalAccessTab,
  "taken-notities": ClientNotesTab,
};

function toId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
}

function toDateLabel(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().slice(0, 10);
}

function preferRealArray(realData, fallbackData) {
  return Array.isArray(realData) && realData.length > 0 ? realData : fallbackData;
}

function mapClient(client, contacts, documents) {
  const defaultContact = contacts.find((item) => item?.isDefault) || contacts[0] || null;

  return {
    id: String(client?._id || clientDetailMockData.client.id),
    bedrijfsnaam: client?.naam || clientDetailMockData.client.bedrijfsnaam,
    klantnummer: client?.klantnummer || client?.nummer || String(client?._id || "").slice(-6) || clientDetailMockData.client.klantnummer,
    status: client?.actief === false ? "inactief" : "actief",
    bezoekadres: [client?.adres, client?.postcode, client?.plaats].filter(Boolean).join(", ") || clientDetailMockData.client.bezoekadres,
    factuuradres: client?.factuuradres || [client?.adres, client?.postcode, client?.plaats].filter(Boolean).join(", ") || clientDetailMockData.client.factuuradres,
    kvkNummer: client?.kvkNummer || clientDetailMockData.client.kvkNummer,
    btwNummer: client?.btwNummer || clientDetailMockData.client.btwNummer,
    hoofdcontactpersoon: defaultContact?.name || client?.contactNaam || clientDetailMockData.client.hoofdcontactpersoon,
    telefoon: client?.telefoon || defaultContact?.phone || clientDetailMockData.client.telefoon,
    email: client?.email || defaultContact?.email || clientDetailMockData.client.email,
    klanttype: client?.klanttype || "opdrachtgever",
    labels: Array.isArray(client?.labels) && client.labels.length > 0 ? client.labels : clientDetailMockData.client.labels,
    documentSamenvatting: {
      totaal: documents.length,
      verlopenBinnenkort: 0,
      ontbrekend: documents.length === 0 ? 1 : 0,
    },
    contactSamenvatting: `${contacts.length} actieve contactpersonen`,
    moneybirdRelatieId: client?.integrations?.moneybird?.id || clientDetailMockData.client.moneybirdRelatieId,
  };
}

function buildSignals({ contacts, workorders, documents }) {
  const out = [];

  if (!contacts.length) {
    out.push({
      id: "sig-no-main-contact",
      type: "waarschuwing",
      titel: "Geen hoofdcontactpersoon ingesteld",
      omschrijving: "Voeg een standaard contactpersoon toe voor snellere afstemming.",
    });
  }

  const oldOpenWorkorder = workorders.find((item) => {
    const open = String(item.status || "").toLowerCase() !== "afgerond";
    const created = item.createdAt ? new Date(item.createdAt) : null;
    if (!open || !created || Number.isNaN(created.getTime())) return false;
    const ageDays = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
    return ageDays > 30;
  });

  if (oldOpenWorkorder) {
    out.push({
      id: "sig-old-workorder",
      type: "let-op",
      titel: "Open werkbon ouder dan 30 dagen",
      omschrijving: `${oldOpenWorkorder.werkbonNummer || oldOpenWorkorder.id} staat al langere tijd open.`,
    });
  }

  if (!documents.length) {
    out.push({
      id: "sig-no-docs",
      type: "waarschuwing",
      titel: "Documentatie ontbreekt",
      omschrijving: "Er zijn nog geen documenten gekoppeld aan deze klant of werkbonnen.",
    });
  }

  out.push({
    id: "sig-compliance-soon",
    type: "compliance",
    titel: "Inspectie of controle verloopt binnenkort",
    omschrijving: "Controleer of geplande inspecties binnen 14 dagen ingepland zijn.",
  });

  return out.slice(0, 4);
}

function mapContacts(contacts) {
  return contacts.map((contact) => ({
    id: String(contact._id || contact.id || `${contact.name}-${contact.email}`),
    naam: contact.name || contact.naam || "Onbekend",
    functie: contact.role || contact.functie || "-",
    email: contact.email || "-",
    telefoon: contact.phone || contact.telefoon || "-",
    type: (contact.role || "technisch").toLowerCase().includes("fact") ? "facturatie" : "technisch",
    isDefault: Boolean(contact.isDefault),
  }));
}

function mapWorkorders(workorders) {
  return workorders.map((workorder) => ({
    id: String(workorder._id || workorder.id),
    werkbonnummer: workorder.werkbonNummer || workorder.nummer || String(workorder._id || "").slice(-6),
    titel: workorder.omschrijving || workorder.title || "Zonder omschrijving",
    status: workorder.status || "onbekend",
    geplandeDatum: toDateLabel(workorder.plannedDate || workorder.createdAt),
    uitvoerder: workorder.assignedTo?.naam || workorder.assignedTo?.name || "Nog niet toegewezen",
    createdAt: workorder.createdAt,
  }));
}

function mapDocuments(workorders) {
  return workorders
    .flatMap((workorder) => {
      const bijlagen = Array.isArray(workorder?.bijlagen) ? workorder.bijlagen : [];
      return bijlagen.map((bijlage) => ({
        id: `${workorder._id}-${bijlage.bestand || bijlage.naam}`,
        bestandsnaam: bijlage.naam || "Document",
        documenttype: bijlage.type || "Bijlage",
        datumUpload: toDateLabel(bijlage.uploadedOp),
        gekoppeldAan: workorder.werkbonNummer || "Werkbon",
      }));
    })
    .slice(0, 25);
}

function mapTasks(tasks) {
  return tasks.map((task) => ({
    id: String(task._id || task.id),
    titel: task.title || task.titel || "Onbekende taak",
    prioriteit: task.prio || "normaal",
    status: task.status || "open",
    deadline: toDateLabel(task.updatedAt || task.createdAt),
  }));
}

function mapOpnames(opnames) {
  return (Array.isArray(opnames) ? opnames : []).map((opname) => ({
    id: String(opname?._id || opname?.id || ""),
    titel: opname?.titel || opname?.title || "Opname",
    status: String(opname?.status || "concept"),
    type: String(opname?.type || "").trim() || "-",
    datum: toDateLabel(opname?.datum || opname?.updatedAt || opname?.createdAt),
    projectNummer: String(opname?.projectNummer || opname?.project?.projectNummer || "").trim(),
    workorderNummer:
      String(
        opname?.workorder?.werkbonNummer ||
          opname?.workorder?.nummer ||
          opname?.gekoppeldeWerkbon?.werkbonNummer ||
          ""
      ).trim(),
  }));
}

function mapActivity(client, workorders, documents) {
  const activity = [];

  if (workorders[0]) {
    activity.push({
      id: `act-wo-${workorders[0].id}`,
      datumTijd: toDateLabel(workorders[0].createdAt),
      actor: "Systeem",
      actie: `Werkbon ${workorders[0].werkbonnummer} bijgewerkt (${workorders[0].status})`,
    });
  }

  if (documents[0]) {
    activity.push({
      id: `act-doc-${documents[0].id}`,
      datumTijd: documents[0].datumUpload,
      actor: "Systeem",
      actie: `Document ${documents[0].bestandsnaam} toegevoegd`,
    });
  }

  activity.push({
    id: "act-client",
    datumTijd: toDateLabel(client?.updatedAt || client?.createdAt),
    actor: "Systeem",
    actie: `Klantrecord ${client?.naam || "onbekend"} geladen`,
  });

  return activity;
}

export default function KlantDetailPage({ initialTab = "overzicht" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [showNieuweWerkbon, setShowNieuweWerkbon] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [projectModalMode, setProjectModalMode] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rawClient, setRawClient] = useState(null);
  const [editingContactId, setEditingContactId] = useState("");

  const [data, setData] = useState({
    ...clientDetailMockData,
    client: {
      ...clientDetailMockData.client,
      id: id || clientDetailMockData.client.id,
    },
    portalAccess: clientDetailMockData.portalAccess,
    suppliers: [],
  });

  const loadClientHub = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [clientsRes, projectsRes, werkbonnenRes, tasksRes, portalRes] = await Promise.allSettled([
        api.get("/clients"),
        api.get("/projects"),
        api.get("/werkbonnen"),
        api.get("/tasks"),
        api.get(`/clients/${id}/portal-access`),
      ]);
      const suppliersPromise = listProcurementSuppliers({ clientId: id, includeInactive: true });
      const opnamesPromise = api.get(`/opnames/client/${id}`).catch(() => ({ data: [] }));

      const clients = clientsRes.status === "fulfilled" && Array.isArray(clientsRes.value?.data)
        ? clientsRes.value.data
        : [];
      const projects = projectsRes.status === "fulfilled" && Array.isArray(projectsRes.value?.data)
        ? projectsRes.value.data
        : [];
      const werkbonnen = werkbonnenRes.status === "fulfilled" && Array.isArray(werkbonnenRes.value?.data)
        ? werkbonnenRes.value.data
        : [];
      const tasks = tasksRes.status === "fulfilled" && Array.isArray(tasksRes.value?.data)
        ? tasksRes.value.data
        : [];
      const portalAccess = portalRes.status === "fulfilled" && portalRes.value?.data
        ? portalRes.value.data
        : clientDetailMockData.portalAccess;
      const suppliersRaw = await suppliersPromise.catch(() => []);
      const opnamesRaw = await opnamesPromise;

      const clientRaw = clients.find((item) => String(item._id) === String(id));
      if (!clientRaw) {
        setRawClient(null);
        setData({
          ...clientDetailMockData,
          client: {
            ...clientDetailMockData.client,
            id: id || clientDetailMockData.client.id,
          },
          portalAccess,
          suppliers: [],
        });
        setError("Klant niet gevonden in API-response. Mock data wordt getoond.");
        return;
      }

      setRawClient(clientRaw);

      const relatedProjects = projects.filter((project) => toId(project.clientId) === String(id));
      const relatedProjectIds = new Set(relatedProjects.map((project) => String(project._id)));

      const relatedWerkbonnen = werkbonnen.filter(
        (workorder) => toId(workorder.clientId) === String(id) || relatedProjectIds.has(toId(workorder.projectId))
      );

      const contactsReal = mapContacts(Array.isArray(clientRaw.contactPersons) ? clientRaw.contactPersons : []);
      const workordersReal = mapWorkorders(relatedWerkbonnen);
      const documentsReal = mapDocuments(relatedWerkbonnen);
      const tasksFiltered = tasks.filter((task) => relatedProjectIds.has(toId(task.projectId)));
      const tasksReal = mapTasks(tasksFiltered);
      const opnames = mapOpnames(Array.isArray(opnamesRaw?.data) ? opnamesRaw.data : []);

      const contacts = preferRealArray(contactsReal, clientDetailMockData.contacts);
      const workorders = preferRealArray(workordersReal, clientDetailMockData.workorders);
      const documents = preferRealArray(documentsReal, clientDetailMockData.documents);
      const notesTasks = preferRealArray(tasksReal, clientDetailMockData.tasks);
      const activityFeed = preferRealArray(mapActivity(clientRaw, workordersReal, documentsReal), clientDetailMockData.activityFeed);
      const mappedClient = mapClient(clientRaw, contacts, documents);

      setData({
        client: mappedClient,
        contacts,
        suppliers: Array.isArray(suppliersRaw) ? suppliersRaw : [],
        workorders,
        documents,
        portalAccess,
        tasks: notesTasks,
        activityFeed,
        signals: buildSignals({ contacts, workorders, documents }),
        opnames,
        openOpdrachten: preferRealArray(
          relatedProjects.map((project) => ({
            id: String(project._id),
            naam: project.naam || project.title || "Onbenoemd project",
            status: project.status || "actief",
            projectNummer: project.projectNummer || project.number || "",
          })),
          clientDetailMockData.openOpdrachten
        ),
        projects: relatedProjects.map((project) => ({
          id: String(project._id),
          naam: project.naam || project.title || "Onbenoemd project",
          status: project.status || "actief",
          projectNummer: project.projectNummer || project.number || "",
        })),
      });
    } catch (err) {
      console.error("Klant-hub laden mislukt:", err);
      setError("Klant-hub laden mislukt. Mock data wordt getoond.");
      setData({
        ...clientDetailMockData,
        client: {
          ...clientDetailMockData.client,
          id: id || clientDetailMockData.client.id,
        },
        suppliers: [],
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleCreateProject = useCallback(async ({ titel, omschrijving, locatie, startDate }) => {
    try {
      setActionLoading(true);
      setActionMessage("");

      await api.post("/projects", {
        naam: titel,
        title: titel,
        omschrijving: omschrijving || "",
        clientId: id,
        locationText: locatie || "",
        startDate: startDate || undefined,
      });

      setProjectModalMode("");
      setActionMessage(projectModalMode === "locatie" ? "Locatie toegevoegd." : "Opdracht toegevoegd.");
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.message || "Opslaan mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [id, loadClientHub, projectModalMode]);

  const handleAddContact = useCallback(async ({ naam, role, phone, email, isDefault }) => {
    try {
      setActionLoading(true);
      setActionMessage("");

      const clientsRes = await api.get("/clients");
      const clients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
      const current = clients.find((item) => String(item._id) === String(id));
      if (!current) throw new Error("Klant niet gevonden.");

      const existing = Array.isArray(current.contactPersons) ? current.contactPersons : [];
      const next = [...existing, { name: naam, role, phone, email, notes: "", active: true, isDefault: Boolean(isDefault) }];
      const normalized = Boolean(isDefault)
        ? next.map((item, index) => ({ ...item, isDefault: index === next.length - 1 }))
        : next;

      await api.put(`/clients/${id}`, { contactPersons: normalized });
      setShowContactModal(false);
      setActionMessage("Contactpersoon toegevoegd.");
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Contactpersoon toevoegen mislukt.");
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [id, loadClientHub]);

  const handleEditContact = useCallback(async ({ naam, role, phone, email, isDefault }) => {
    try {
      if (!editingContactId) {
        const selectionError = new Error("Geen contactpersoon geselecteerd.");
        setActionMessage(selectionError.message);
        throw selectionError;
      }

      setActionLoading(true);
      setActionMessage("");

      const clientsRes = await api.get("/clients");
      const clients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
      const current = clients.find((item) => String(item._id) === String(id));
      if (!current) throw new Error("Klant niet gevonden.");

      const existing = Array.isArray(current.contactPersons) ? current.contactPersons : [];
      let matched = false;
      const next = existing.map((item) => {
        if (String(item._id || item.id || "") !== String(editingContactId)) {
          return item;
        }
        matched = true;
        return {
          ...item,
          name: naam,
          role,
          phone,
          email,
          isDefault: Boolean(isDefault),
        };
      });

      if (!matched) {
        throw new Error("Contactpersoon niet gevonden.");
      }

      const normalized = Boolean(isDefault)
        ? next.map((item) => ({
            ...item,
            isDefault: String(item._id || item.id || "") === String(editingContactId),
          }))
        : next;

      await api.put(`/clients/${id}`, { contactPersons: normalized });
      setShowEditContactModal(false);
      setEditingContactId("");
      setActionMessage("Contactpersoon bijgewerkt.");
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Contactpersoon bijwerken mislukt.");
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [editingContactId, id, loadClientHub]);

  const handleAddSupplier = useCallback(
    async (payload) => {
      try {
        setActionLoading(true);
        setActionMessage("");
        await createProcurementSupplier({
          ...(payload || {}),
          clientId: id,
        });
        setActionMessage("Leverancier toegevoegd bij deze klant.");
        await loadClientHub();
      } catch (err) {
        setActionMessage(err?.response?.data?.message || "Leverancier toevoegen mislukt.");
      } finally {
        setActionLoading(false);
      }
    },
    [id, loadClientHub]
  );

  const handleUpdateSupplier = useCallback(
    async (supplierId, payload) => {
      try {
        setActionLoading(true);
        setActionMessage("");
        await updateProcurementSupplier(supplierId, {
          ...(payload || {}),
          clientId: id,
        });
        setActionMessage("Leverancier bijgewerkt.");
        await loadClientHub();
      } catch (err) {
        setActionMessage(err?.response?.data?.message || "Leverancier bijwerken mislukt.");
      } finally {
        setActionLoading(false);
      }
    },
    [id, loadClientHub]
  );

  const handleDocumentUpload = useCallback(async ({ workorderId, file }) => {
    try {
      if (!file) {
        setActionMessage("Selecteer eerst een bestand.");
        return;
      }
      setActionLoading(true);
      setActionMessage("");

      await uploadWorkorderAttachment(workorderId, file);

      setShowDocumentModal(false);
      setActionMessage("Document geüpload.");
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.message || "Document upload mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [loadClientHub]);

  const editingContact = useMemo(() => {
    if (!editingContactId) return null;
    const contacts = Array.isArray(rawClient?.contactPersons) ? rawClient.contactPersons : [];
    return contacts.find((item) => String(item._id || item.id || "") === String(editingContactId)) || null;
  }, [editingContactId, rawClient?.contactPersons]);

  const visibleTabs = useMemo(() => {
    const permissionSet = new Set(
      (Array.isArray(user?.permissions) ? user.permissions : [])
        .map((item) => String(item || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const canViewPricing = permissionSet.has("pricing.view") || permissionSet.has("pricing.manage");
    const canViewAssets = permissionSet.has("assets.view");
    return DEFAULT_TABS.filter((tab) => {
      if (tab.key === "commercieel") return canViewPricing;
      if (tab.key === "assets") return canViewAssets;
      return true;
    });
  }, [user?.permissions]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || "overzicht");
    }
  }, [activeTab, visibleTabs]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    navigate(tab === "assets" ? `/klanten/${id}/assets` : `/klanten/${id}`);
  }, [id, navigate]);

  const handleEditClient = useCallback(async (payload) => {
    try {
      setActionLoading(true);
      setActionMessage("");

      const existingContacts = Array.isArray(rawClient?.contactPersons) ? rawClient.contactPersons : [];
      const normalizedEmail = String(payload?.email || "").trim();
      const defaultIndex = existingContacts.findIndex((item) => item?.isDefault);
      const fallbackIndex = defaultIndex >= 0 ? defaultIndex : (existingContacts.length > 0 ? 0 : -1);
      const nextContacts =
        fallbackIndex >= 0
          ? existingContacts.map((item, index) => (
            index === fallbackIndex
              ? {
                  ...item,
                  email: normalizedEmail,
                }
              : item
          ))
          : existingContacts;

      const requestPayload = {
        ...payload,
        ...(fallbackIndex >= 0 ? { contactPersons: nextContacts } : {}),
      };

      await api.put(`/clients/${id}`, requestPayload);
      setShowEditModal(false);
      setActionMessage("Klant bijgewerkt.");
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.error || "Klant bijwerken mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [id, loadClientHub, rawClient]);

  const handleExportClient = useCallback(async ({ format }) => {
    try {
      setActionLoading(true);
      setActionMessage("");
      await exportClientHub({
        clientHub: data,
        format,
        filenameBase: `klant-${data.client.klantnummer || data.client.id}-${new Date().toISOString().slice(0, 10)}`,
      });
      setShowExportModal(false);
      setActionMessage(`Export gestart (${String(format || "").toUpperCase()}).`);
    } catch {
      setActionMessage("Exporteren mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [data]);

  const handleExportClientPackage = useCallback(async () => {
    try {
      setActionLoading(true);
      setActionMessage("");

      const res = await api.get(`/clients/${id}/export-package`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: res?.headers?.["content-type"] || "application/gzip",
      });

      const disposition = String(res?.headers?.["content-disposition"] || "");
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      const fallbackName = `client-dossier-${String(id || "").slice(-6)}-${new Date()
        .toISOString()
        .slice(0, 10)}.tar.gz`;
      const filename = filenameMatch?.[1] || fallbackName;

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);

      setActionMessage("Klantdossier export gedownload.");
    } catch (err) {
      setActionMessage(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Klantdossier export mislukt."
      );
    } finally {
      setActionLoading(false);
    }
  }, [id]);

  const handleImportClients = useCallback(async ({ rows, strategy }) => {
    try {
      setActionLoading(true);
      setActionMessage("");

      const res = await api.post("/clients/import", { rows, strategy });
      const summary = res?.data?.summary || {};
      setShowImportModal(false);
      setActionMessage(`Import klaar: ${Number(summary.created || 0)} nieuw, ${Number(summary.updated || 0)} bijgewerkt, ${Number(summary.skipped || 0)} overgeslagen, ${Number(summary.failed || 0)} mislukt.`);
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.error || "Import mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [loadClientHub]);

  const runPortalAction = useCallback(async (request, successMessage) => {
    try {
      setActionLoading(true);
      setActionMessage("");
      const response = await request();
      setActionMessage(response?.data?.message || successMessage);
      await loadClientHub();
    } catch (err) {
      setActionMessage(err?.response?.data?.error || "Portaalactie mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [loadClientHub]);

  const pickAvailableContact = useCallback(() => {
    const available = Array.isArray(data?.portalAccess?.availableContacts) ? data.portalAccess.availableContacts : [];
    return available[0] || null;
  }, [data?.portalAccess?.availableContacts]);

  const handlePortalActivateQuick = useCallback(async () => {
    const contact = pickAvailableContact();
    if (!contact) {
      setActionMessage("Geen beschikbare contactpersoon om portaaltoegang te activeren.");
      return;
    }
    await runPortalAction(
      () => api.post(`/clients/${id}/portal-access/activate`, { contactId: contact.contactId, role: "opdrachtgever" }),
      "Portaaltoegang geactiveerd."
    );
  }, [id, pickAvailableContact, runPortalAction]);

  const handlePortalInviteQuick = useCallback(async () => {
    const contact = pickAvailableContact();
    if (!contact) {
      setActionMessage("Geen beschikbare contactpersoon om uit te nodigen.");
      return;
    }
    await runPortalAction(
      () => api.post(`/clients/${id}/portal-access/invite`, { contactId: contact.contactId, role: "opdrachtgever" }),
      "Uitnodiging verstuurd."
    );
  }, [id, pickAvailableContact, runPortalAction]);

  const handlePortalOpen = useCallback(() => {
    const url = data?.portalAccess?.summary?.portalUrl;
    if (!url) {
      setActionMessage("Portaal is nog niet actief voor deze klant.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, [data?.portalAccess?.summary?.portalUrl]);

  const handleDeactivateClient = useCallback(async () => {
    const sure = window.confirm("Weet je zeker dat je deze klant inactief wilt zetten?");
    if (!sure) return;
    try {
      setActionLoading(true);
      setActionMessage("");
      await api.delete(`/clients/${id}`);
      setActionMessage("Klant is inactief gezet.");
      navigate("/klanten");
    } catch (err) {
      setActionMessage(err?.response?.data?.error || "Klant inactief zetten mislukt.");
    } finally {
      setActionLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadClientHub();
  }, [loadClientHub]);

  const ActiveTabComponent = TAB_COMPONENTS[activeTab] || ClientOverviewTab;
  const pageTitle = useMemo(() => `Klantdetail: ${data.client.bedrijfsnaam}`, [data.client.bedrijfsnaam]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-[1480px] space-y-5">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-900 p-2 text-white"><Building2 size={18} /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Klant-hub</p>
              <h1 className="text-2xl font-semibold text-slate-900">{pageTitle}</h1>
            </div>
          </div>
          {error && <p className="mt-3 text-sm text-amber-700">{error}</p>}
        </header>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">Klantgegevens laden...</div>
        ) : (
          <main className={activeTab === "assets" ? "space-y-4" : "grid gap-5 lg:grid-cols-[350px_1fr]"}>
            {activeTab !== "assets" ? <ClientSummaryCard client={data.client} /> : null}

            <section className="space-y-4">
              <ClientActionBar
                onNieuweWerkbon={() => setShowNieuweWerkbon(true)}
                onNieuweOpdracht={() => setProjectModalMode("opdracht")}
                onContactToevoegen={() => setShowContactModal(true)}
                onDocumentUploaden={() => setShowDocumentModal(true)}
                onLocatieToevoegen={() => setProjectModalMode("locatie")}
                onBewerken={() => setShowEditModal(true)}
                onImporteren={() => setShowImportModal(true)}
                onExporteren={() => setShowExportModal(true)}
                onDossierExporteren={handleExportClientPackage}
                onVerversen={loadClientHub}
                onDeactiveren={handleDeactivateClient}
              />

              {actionMessage && <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">{actionMessage}</div>}

              <ClientTabs activeTab={activeTab} onChange={handleTabChange} tabs={visibleTabs} />

              <ActiveTabComponent
                client={data.client}
                rawClient={rawClient}
                contacts={data.contacts}
                suppliers={Array.isArray(data.suppliers) ? data.suppliers : []}
                workorders={data.workorders}
                documents={data.documents}
                tasks={data.tasks}
                activityFeed={data.activityFeed}
                data={data}
                portalAccess={data.portalAccess}
                loading={actionLoading}
                onCreateSupplier={handleAddSupplier}
                onUpdateSupplier={handleUpdateSupplier}
                onOpenPortalAccess={() => setActiveTab("portaaltoegang")}
                onPortalActivate={handlePortalActivateQuick}
                onPortalInvite={handlePortalInviteQuick}
                onPortalOpen={handlePortalOpen}
                onEditContact={(contact) => {
                  setEditingContactId(String(contact?.id || ""));
                  setShowEditContactModal(true);
                }}
                onInvite={async (contactId, role) => runPortalAction(() => api.post(`/clients/${id}/portal-access/invite`, { contactId, role }), "Uitnodiging verstuurd.")}
                onActivate={async (contactId, role) => runPortalAction(() => api.post(`/clients/${id}/portal-access/activate`, { contactId, role }), "Portaaltoegang geactiveerd.")}
                onUpdate={async (contactId, payload) => runPortalAction(() => api.put(`/clients/${id}/portal-access/${contactId}`, payload), "Portaaltoegang bijgewerkt.")}
                onResendInvite={async (contactId) => runPortalAction(() => api.post(`/clients/${id}/portal-access/${contactId}/resend-invite`), "Uitnodiging opnieuw verstuurd.")}
                onBlock={async (contactId) => runPortalAction(() => api.post(`/clients/${id}/portal-access/${contactId}/block`), "Portaalgebruiker geblokkeerd.")}
                onUnblock={async (contactId) => runPortalAction(() => api.post(`/clients/${id}/portal-access/${contactId}/unblock`), "Portaalgebruiker gedeblokkeerd.")}
                onRevoke={async (contactId) => runPortalAction(() => api.post(`/clients/${id}/portal-access/${contactId}/revoke`), "Portaaltoegang ingetrokken.")}
              />
            </section>
          </main>
        )}
      </div>

      {showNieuweWerkbon && (
        <NieuweWerkbonModal
          initialClientId={String(data.client.id)}
          onClose={() => setShowNieuweWerkbon(false)}
          onCreated={() => {
            setShowNieuweWerkbon(false);
            loadClientHub();
          }}
        />
      )}

      <QuickActionProjectModal
        open={Boolean(projectModalMode)}
        mode={projectModalMode}
        loading={actionLoading}
        onClose={() => setProjectModalMode("")}
        onSubmit={handleCreateProject}
      />

      <QuickActionContactModal
        open={showContactModal}
        loading={actionLoading}
        onClose={() => setShowContactModal(false)}
        onSubmit={handleAddContact}
      />

      <QuickActionContactModal
        open={showEditContactModal}
        loading={actionLoading}
        initialContact={editingContact}
        title="Contactpersoon bewerken"
        onClose={() => {
          setShowEditContactModal(false);
          setEditingContactId("");
        }}
        onSubmit={handleEditContact}
      />

      <QuickActionDocumentModal
        open={showDocumentModal}
        loading={actionLoading}
        workorders={data.workorders}
        onClose={() => setShowDocumentModal(false)}
        onSubmit={handleDocumentUpload}
      />

      <QuickActionEditClientModal
        open={showEditModal}
        loading={actionLoading}
        client={rawClient}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditClient}
      />

      <QuickActionSingleExportModal
        open={showExportModal}
        loading={actionLoading}
        onClose={() => setShowExportModal(false)}
        onSubmit={handleExportClient}
      />

      <ClientImportModal
        open={showImportModal}
        loading={actionLoading}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportClients}
      />
    </div>
  );
}
